import React, { useState, useEffect, useRef } from 'react';
import { Socket } from 'socket.io-client';
import { useLobby } from '../hooks/useLobby';
import { PlayerList } from './PlayerList';
import { InvitationModal } from './InvitationModal';
import { NeonButton } from './NeonButton';
import { RetroGrid } from './RetroGrid';
import { Leaderboard } from './Leaderboard';
import { PlayerHistory } from './PlayerHistory';
import { PlayerStats } from './PlayerStats';
import { LocalStats } from './LocalStats';
import { ThemeSelector } from './ThemeSelector';
import { Logo } from './Logo';
import { useToast } from './Toast';
import { LocalStorageService } from '../services/localStorageService';
import {
  InvitationExpiredPayload,
  InvitationReceivedPayload,
  InvitationRejectedPayload,
  LobbyJoinedPayload,
  PlayersUpdatePayload
} from '../types/socket';

interface LobbyProps {
  socket: Socket | null;
}

export const Lobby: React.FC<LobbyProps> = ({ socket }) => {
  const [playerName, setPlayerName] = useState('');
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [showLocalStats, setShowLocalStats] = useState(false);
  const [showThemeSelector, setShowThemeSelector] = useState(false);
  const { showToast } = useToast();
  const resumeRequestedRef = useRef(false);
  const {
    onlinePlayers,
    currentPlayer,
    pendingInvitation,
    isInLobby,
    error,
    joinLobby,
    invitePlayer,
    acceptInvitation,
    rejectInvitation,
    setOnlinePlayers,
    setCurrentPlayer,
    setPendingInvitation,
    setIsInLobby
  } = useLobby(socket);

  const handleJoinQueue = () => {
    if (!socket || !currentPlayer) return;
    socket.emit('join-queue', currentPlayer.name);
  };

  const handleJoinLobby = () => {
    if (playerName.trim()) {
      const prefs = LocalStorageService.getPlayerPreferences();
      if (prefs.rememberName) {
        LocalStorageService.setPlayerName(playerName.trim());
      }
      joinLobby(playerName.trim());
    }
  };

  useEffect(() => {
    const prefs = LocalStorageService.getPlayerPreferences();
    if (prefs.rememberName) {
      const savedName = LocalStorageService.getPlayerName();
      if (savedName) {
        setPlayerName(savedName);
      }
    }
  }, []);

  useEffect(() => {
    if (!socket) return;

    socket.on('lobby-joined', (player: LobbyJoinedPayload) => {
      setCurrentPlayer(player);
      setIsInLobby(true);
      showToast(`¡Bienvenido al lobby, ${player.name}!`, 'success');
    });

    socket.on('players-update', (players: PlayersUpdatePayload) => {
      const selfId = socket?.id || currentPlayer?.id;
      const filteredPlayers = selfId
        ? players.filter(p => p.id !== selfId)
        : players;
      setOnlinePlayers(filteredPlayers);
    });

    socket.on('invitation-received', (invitation: InvitationReceivedPayload) => {
      setPendingInvitation(invitation);
      showToast(`${invitation.from.name} te ha invitado a jugar`, 'info', 5000);
    });

    socket.on('invitation-rejected', (data: InvitationRejectedPayload) => {
      showToast('Tu invitación fue rechazada', 'warning');
      console.log('Invitación rechazada por:', data.from);
    });

    socket.on('invitation-expired', (_data: InvitationExpiredPayload) => {
      setPendingInvitation(null);
      showToast('La invitación expiró', 'warning');
    });

    socket.on('invite-error', (errorMessage: string) => {
      showToast(errorMessage, 'error');
      console.log('Error de invitación:', errorMessage);
    });

    socket.on('queue-joined', () => {
      showToast('Buscando oponente...', 'info');
    });

    socket.on('queue-error', (errorMessage: string) => {
      showToast(errorMessage, 'error');
    });

    return () => {
      socket.off('lobby-joined');
      socket.off('players-update');
      socket.off('invitation-received');
      socket.off('invitation-rejected');
      socket.off('invitation-expired');
      socket.off('invite-error');
      socket.off('queue-joined');
      socket.off('queue-error');
    };
  }, [socket, currentPlayer, setOnlinePlayers, setCurrentPlayer, setPendingInvitation, setIsInLobby, showToast]);

  useEffect(() => {
    if (!socket) return;

    if (currentPlayer?.status === 'in-game') {
      if (!resumeRequestedRef.current) {
        resumeRequestedRef.current = true;
        socket.emit('get-active-session');
        showToast('Reanudando partida...', 'info');
      }
      return;
    }

    resumeRequestedRef.current = false;
  }, [socket, currentPlayer?.status, showToast]);

  if (!isInLobby) {
    return (
      <RetroGrid>
        <div className="flex flex-col items-center justify-start min-h-screen p-4 pt-6">
          <div className="w-10 h-10 sm:w-12 sm:h-12 mb-2">
            <Logo className="w-full h-full" />
          </div>
          <div className="text-center mb-6">
            <h1 className="font-retro text-3xl sm:text-4xl md:text-6xl font-bold text-neon-pink mb-2 animate-glow">
              GATO RETRO
            </h1>
            <p className="font-retro text-neon-cyan text-base sm:text-lg md:text-xl">
              LOBBY MULTIJUGADOR
            </p>
          </div>

          <div className="ui-section">
            <div className="ui-card">
              <h2 className="font-retro text-neon-yellow text-2xl mb-6 text-center">
                ENTRAR AL LOBBY
              </h2>
              
              <div className="mb-6">
                <label htmlFor="playerName" className="block font-retro text-neon-cyan mb-2">
                  NOMBRE DE GUERRERO:
                </label>
                <input
                  id="playerName"
                  name="playerName"
                  type="text"
                  value={playerName}
                  onChange={(e) => setPlayerName(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleJoinLobby()}
                  maxLength={20}
                  className="
                    w-full px-3 sm:px-4 py-3 sm:py-4 
                    bg-retro-black border-2 border-neon-cyan/50
                    font-retro text-neon-yellow text-base sm:text-lg rounded-lg
                    focus:border-neon-cyan focus:outline-none focus:shadow-neon-cyan
                    transition-all duration-300
                  "
                  placeholder="Ingresa tu nombre..."
                />
              </div>

              <NeonButton
                onClick={handleJoinLobby}
                disabled={!playerName.trim()}
                color="cyan"
                className="w-full text-base sm:text-lg mb-3"
              >
                ENTRAR AL LOBBY
              </NeonButton>

              <button
                onClick={() => setShowThemeSelector(true)}
                className="w-full ui-button bg-orange-500/20 hover:bg-orange-500/30 border-2 border-orange-400 text-orange-400 hover:shadow-orange-400 mb-2"
              >
                🎨 CAMBIAR TEMA
              </button>

              <button
                onClick={() => setShowLocalStats(true)}
                className="w-full ui-button bg-purple-500/20 hover:bg-purple-500/30 border-2 border-purple-400 text-purple-400 hover:shadow-purple-400"
              >
                💾 VER ESTADÍSTICAS LOCALES
              </button>
            </div>
          </div>
        </div>
      </RetroGrid>
    );
  }

  return (
    <RetroGrid>
      <div className="min-h-screen p-4">
        <div className="text-center mb-4 sm:mb-6">
          <div className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-2 sm:mb-3">
            <Logo className="w-full h-full" />
          </div>
          <h1 className="font-retro text-2xl sm:text-3xl md:text-4xl font-bold text-neon-pink mb-1 sm:mb-2">
            GATO RETRO - LOBBY
          </h1>
          {currentPlayer && (
            <div className="font-retro text-neon-cyan text-xs sm:text-sm mb-3 sm:mb-4">
              {currentPlayer.name} ({currentPlayer.symbol})
            </div>
          )}

          <div className="grid grid-cols-2 sm:flex sm:flex-wrap justify-center gap-2 mt-3 sm:mt-4">
            <button
              onClick={handleJoinQueue}
              className="ui-button bg-neon-cyan/20 hover:bg-neon-cyan/30 border-2 border-neon-cyan text-neon-cyan hover:shadow-neon-cyan col-span-2 sm:col-auto"
              disabled={!currentPlayer || currentPlayer.status !== 'available'}
            >
              ⚡ MATCH RÁPIDO
            </button>
            <button
              onClick={() => setShowLeaderboard(true)}
              className="ui-button bg-neon-yellow/20 hover:bg-neon-yellow/30 border-2 border-neon-yellow text-neon-yellow hover:shadow-neon-yellow"
            >
              🏆 RANKING
            </button>
            <button
              onClick={() => setShowStats(true)}
              className="ui-button bg-neon-green/20 hover:bg-neon-green/30 border-2 border-neon-green text-neon-green hover:shadow-neon-green"
            >
              📈 MIS STATS
            </button>
            <button
              onClick={() => setShowHistory(true)}
              className="ui-button bg-neon-cyan/20 hover:bg-neon-cyan/30 border-2 border-neon-cyan text-neon-cyan hover:shadow-neon-cyan"
            >
              📊 HISTORIAL
            </button>
            <button
              onClick={() => setShowLocalStats(true)}
              className="ui-button bg-purple-500/20 hover:bg-purple-500/30 border-2 border-purple-400 text-purple-400 hover:shadow-purple-400"
            >
              💾 LOCAL
            </button>
            <button
              onClick={() => setShowThemeSelector(true)}
              className="ui-button bg-orange-500/20 hover:bg-orange-500/30 border-2 border-orange-400 text-orange-400 hover:shadow-orange-400 col-span-2 sm:col-auto"
            >
              🎨 TEMAS
            </button>
          </div>
        </div>

        {showLeaderboard && (
          <Leaderboard
            socket={socket}
            onClose={() => setShowLeaderboard(false)}
          />
        )}

        {showStats && currentPlayer && (
          <PlayerStats
            socket={socket}
            username={currentPlayer.name}
            onClose={() => setShowStats(false)}
          />
        )}

        {showHistory && currentPlayer && (
          <PlayerHistory
            socket={socket}
            username={currentPlayer.name}
            onClose={() => setShowHistory(false)}
          />
        )}

        {showLocalStats && (
          <LocalStats
            playerName={currentPlayer?.name || playerName}
            onClose={() => setShowLocalStats(false)}
          />
        )}

        {showThemeSelector && (
          <ThemeSelector
            onClose={() => setShowThemeSelector(false)}
          />
        )}

        {error && (
          <div className="bg-retro-dark/80 border-2 border-neon-pink rounded-lg p-4 mb-6 text-center max-w-md mx-auto">
            <p className="font-retro text-neon-pink">{error}</p>
          </div>
        )}

        <div className="max-w-lg mx-auto">
          <PlayerList
            players={onlinePlayers}
            onInvitePlayer={invitePlayer}
            currentPlayerId={currentPlayer?.id || ''}
          />
        </div>

        <InvitationModal
          invitation={pendingInvitation}
          onAccept={acceptInvitation}
          onReject={rejectInvitation}
        />
      </div>
    </RetroGrid>
  );
};
